import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { RouteFallback } from './RouteFallback';
import { RouteError } from './RouteError';

// Code-split feature pages so the initial bundle stays lean.
const OverviewPage = lazy(() =>
  import('@/features/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
const ModelsPage = lazy(() =>
  import('@/features/models/ModelsPage').then((m) => ({ default: m.ModelsPage })),
);
const ModelDetailPage = lazy(() =>
  import('@/features/models/ModelDetailPage').then((m) => ({ default: m.ModelDetailPage })),
);
const AdminClientsPage = lazy(() =>
  import('@/features/admin/AdminClientsPage').then((m) => ({ default: m.AdminClientsPage })),
);
const AdminClientDetailPage = lazy(() =>
  import('@/features/admin/AdminClientDetailPage').then((m) => ({ default: m.AdminClientDetailPage })),
);
const AdminPricingPage = lazy(() =>
  import('@/features/admin/AdminPricingPage').then((m) => ({ default: m.AdminPricingPage })),
);
const AdminProvidersPage = lazy(() =>
  import('@/features/admin/AdminProvidersPage').then((m) => ({ default: m.AdminProvidersPage })),
);
const AdminModelRequestsPage = lazy(() =>
  import('@/features/admin/AdminModelRequestsPage').then((m) => ({ default: m.AdminModelRequestsPage })),
);
const AdminApiLogsPage = lazy(() =>
  import('@/features/admin/AdminApiLogsPage').then((m) => ({ default: m.AdminApiLogsPage })),
);
const ClientOverviewPage = lazy(() =>
  import('@/features/client/ClientOverviewPage').then((m) => ({ default: m.ClientOverviewPage })),
);
const ClientModelsPage = lazy(() =>
  import('@/features/client/ClientModelsPage').then((m) => ({ default: m.ClientModelsPage })),
);
const ClientUsagePage = lazy(() =>
  import('@/features/client/ClientUsagePage').then((m) => ({ default: m.ClientUsagePage })),
);
const ClientDocsPage = lazy(() =>
  import('@/features/client/ClientDocsPage').then((m) => ({ default: m.ClientDocsPage })),
);
const ClientSolutionsPage = lazy(() =>
  import('@/features/client/ClientSolutionsPage').then((m) => ({ default: m.ClientSolutionsPage })),
);
const ClientBillsPage = lazy(() =>
  import('@/features/client/ClientBillsPage').then((m) => ({ default: m.ClientBillsPage })),
);
const ChangePasswordPage = lazy(() =>
  import('@/features/client/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
);

function lazyPage(node: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      // Focused, full-screen (no app shell) — first-login password change.
      { path: '/client/change-password', element: lazyPage(<ChangePasswordPage />) },
      {
        element: <AppShell />,
        // A render error in any child page shows a recoverable panel, never a
        // black screen.
        errorElement: <RouteError />,
        children: [
          { path: '/', element: lazyPage(<OverviewPage />) },
          { path: '/models', element: lazyPage(<ModelsPage />) },
          { path: '/models/:modelId', element: lazyPage(<ModelDetailPage />) },
          // Super Admin
          { path: '/admin/clients', element: lazyPage(<AdminClientsPage />) },
          { path: '/admin/clients/:id', element: lazyPage(<AdminClientDetailPage />) },
          { path: '/admin/requests', element: lazyPage(<AdminModelRequestsPage />) },
          { path: '/admin/api-logs', element: lazyPage(<AdminApiLogsPage />) },
          { path: '/admin/model-catalog', element: lazyPage(<AdminPricingPage />) },
          { path: '/admin/pricing', element: <Navigate to="/admin/model-catalog" replace /> },
          { path: '/admin/providers', element: lazyPage(<AdminProvidersPage />) },
          // Client portal
          { path: '/client', element: lazyPage(<ClientOverviewPage />) },
          { path: '/client/models', element: lazyPage(<ClientModelsPage />) },
          { path: '/client/solutions', element: lazyPage(<ClientSolutionsPage />) },
          { path: '/client/usage', element: lazyPage(<ClientUsagePage />) },
          { path: '/client/bills', element: lazyPage(<ClientBillsPage />) },
          { path: '/client/docs', element: lazyPage(<ClientDocsPage />) },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
